import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Tabs, Table, Button, Row, Col, Statistic, message, Spin, Empty, Image
} from 'antd';
import { EditOutlined, SaveOutlined } from '@ant-design/icons';
import axios from 'axios';
import UniversalModuleModal from './UniversalModuleModal';
import { MODULE_MODAL_CONFIGS } from './moduleModalConfigs';



const LeastSquares = ({ fileId, fileName, setSelectedFile, initialConfig }) => {
    const [activeTab, setActiveTab] = useState('input');
    const [dataPoints, setDataPoints] = useState([]);
    const [results, setResults] = useState(null);
    const [chartUrl, setChartUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);

    const getAuthToken = () => localStorage.getItem('access_token');

    useEffect(() => {
        if (fileId) {
            loadFileData(fileId);
        } else if (initialConfig) {
            // Initialize from modal config
            if (initialConfig.data_points) {
                setDataPoints(initialConfig.data_points.map((p, i) => ({ key: i, ...p })));
            }
            // Auto-calculate if we have data but no fileId yet (not saved, just local state)
            // Actually, we usually want to save immediately or just show input.
            // Let's just set input data.
        }
    }, [fileId, initialConfig]);

    const loadFileData = async (id) => {
        try {
            const token = getAuthToken();
            if (!token) return;

            setIsLoading(true);
            const response = await axios.get(`/api/retrieve_leastSquares/${id}/`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            console.log('Least Squares data loaded:', response.data);

            let { input_data, output_data, chart_url } = response.data;

            // Handle Input Data
            if (input_data) {
                if (input_data.data_points) {
                    setDataPoints(input_data.data_points.map((p, i) => ({ key: i, ...p })));
                } else if (input_data.observations) {
                    // Fallback for detailed format data
                    setDataPoints(input_data.observations.map((obs, i) => ({
                        key: i,
                        x: obs.X1 || obs.x || 0,
                        y: obs.Y || obs.y || 0
                    })));
                }
            }

            // Handle Output Data
            if (output_data) {
                // Map detailed keys to simple keys if needed
                if (!output_data.equation && output_data.regression_equation) {
                    output_data.equation = output_data.regression_equation;
                }
                if (output_data.coefficients) {
                    if (output_data.intercept === undefined) output_data.intercept = output_data.coefficients.intercept;
                    if (output_data.slope === undefined && output_data.coefficients.variables) {
                        const firstVar = Object.keys(output_data.coefficients.variables)[0];
                        output_data.slope = output_data.coefficients.variables[firstVar];
                    }
                }

                setResults(output_data);

                // Handle Chart URL (could be a string or JSON dict)
                if (typeof chart_url === 'string' && chart_url.startsWith('{')) {
                    try {
                        const charts = JSON.parse(chart_url);
                        setChartUrl(charts.residuals || charts[Object.keys(charts)[0]]);
                    } catch (e) {
                        setChartUrl(chart_url);
                    }
                } else {
                    setChartUrl(chart_url);
                }

                setActiveTab('output');
            }

            setIsSaved(true);
            setIsLoading(false);
        } catch (error) {
            console.error('Error loading file:', error);
            message.error('Failed to load analysis data');
            setIsLoading(false);
        }
    };

    const saveData = async (config = null) => {
        try {
            const token = getAuthToken();
            if (!token) {
                message.error("User is not authenticated.");
                return;
            }

            setIsLoading(true);

            // If config is passed (from modal), use it. Otherwise use current state.
            // If called from button, we use current state dataPoints.
            const currentPoints = config ? config.data_points : dataPoints;

            if (!currentPoints || currentPoints.length < 2) {
                message.warning("Insufficient data points. Please input at least 2 points.");
                setIsLoading(false);
                return;
            }

            const payload = {
                name: config ? config.name : (fileName || 'Least Squares Analysis'),
                input_data: {
                    data_points: currentPoints.map(({ x, y }) => ({ x: x || 0, y: y || 0 }))
                }
            };

            const headers = { Authorization: `Bearer ${token}` };
            let response;

            if (fileId) {
                response = await axios.put(`/api/update_leastSquares/${fileId}/`, payload, { headers });
            } else {
                response = await axios.post('/api/save_leastSquares/', payload, { headers });
                // If it's a new file, we might want to update the parent context's selectedFile
                // But usually the parent handles refreshing the file list.
                // For now, we update local state.
            }

            const { output_data, chart_url } = response.data;
            setResults(output_data);
            setChartUrl(chart_url);

            // Update local inputs if they changed (e.g. via modal)
            if (config) {
                setDataPoints(config.data_points.map((p, i) => ({ key: i, ...p })));
            }

            setActiveTab('output');
            setIsSaved(true);
            message.success('Analysis saved successfully');
            setIsLoading(false);

        } catch (error) {
            console.error('Error saving data:', error);
            const errorMsg = error.response?.data?.error || 'Failed to save analysis';
            // Handle if error is an object/dict (e.g. serializer errors)
            const displayMsg = typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg;
            message.error(displayMsg);
            setIsLoading(false);
        }
    };

    const handleEditConfig = (config) => {
        // Called when modal confirms
        setIsEditModalVisible(false);
        saveData(config);
    };

    const showEditModal = () => {
        setIsEditModalVisible(true);
    };

    const inputColumns = [
        { title: 'X Value', dataIndex: 'x', key: 'x' },
        { title: 'Y Value', dataIndex: 'y', key: 'y' },
    ];

    const renderInputTab = () => (
        <div>
            <Card
                title="Data Points"
                extra={
                    <Button icon={<EditOutlined />} onClick={showEditModal}>
                        Edit Data
                    </Button>
                }
            >
                <Table
                    dataSource={dataPoints}
                    columns={inputColumns}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ y: 300 }}
                />
                <div style={{ marginTop: 16, textAlign: 'right' }}>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={() => saveData()}
                        loading={isLoading}
                        disabled={dataPoints.length < 2}
                    >
                        Calculate & Save
                    </Button>
                </div>
            </Card>

            <UniversalModuleModal
                isVisible={isEditModalVisible}
                onClose={() => setIsEditModalVisible(false)}
                onConfirm={(config) => {
                    setIsEditModalVisible(false);
                    if (config.numPoints) {
                        const newCount = config.numPoints;
                        let newPoints = [...dataPoints];
                        if (newPoints.length < newCount) {
                            while (newPoints.length < newCount) {
                                newPoints.push({ x: newPoints.length + 1, y: 0 });
                            }
                        } else if (newPoints.length > newCount) {
                            newPoints = newPoints.slice(0, newCount);
                        }
                        setDataPoints(newPoints.map((p, i) => ({ ...p, key: i })));
                    }
                }}
                config={MODULE_MODAL_CONFIGS.least_squares}
                initialValues={{
                    numPoints: dataPoints.length || 6
                }}
            />
        </div>
    );

    const renderOutputTab = () => {
        if (!results) {
            return <Empty description="No results generated yet. Please save data to calculate." />;
        }

        return (
            <div style={{ padding: 16 }}>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Card title="Regression Equation">
                            <Statistic
                                title="Linear Equation"
                                value={results.equation}
                                valueStyle={{ color: '#3f8600' }}
                            />
                            <Row gutter={16} style={{ marginTop: 16 }}>
                                <Col span={12}>
                                    <Statistic title="Slope (b)" value={results.slope} precision={4} />
                                </Col>
                                <Col span={12}>
                                    <Statistic title="Intecept (a)" value={results.intercept} precision={4} />
                                </Col>
                            </Row>
                        </Card>
                    </Col>

                    <Col span={24}>
                        <Card title="Regression Summary">
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Statistic title="Count (n)" value={results.data_summary?.count} />
                                </Col>
                                <Col span={8}>
                                    <Statistic title="Mean X" value={results.data_summary?.x_mean} precision={4} />
                                </Col>
                                <Col span={8}>
                                    <Statistic title="Mean Y" value={results.data_summary?.y_mean} precision={4} />
                                </Col>
                            </Row>
                        </Card>
                    </Col>

                    {chartUrl && (
                        <Col span={24}>
                            <Card title="Regression Chart">
                                <div style={{ textAlign: 'center' }}>
                                    <Image src={chartUrl} alt="Regression Chart" style={{ maxWidth: '100%' }} />
                                </div>
                            </Card>
                        </Col>
                    )}
                </Row>
            </div>
        );
    };

    return (
        <Card
            title={fileName || "Least Squares Analysis"}
            style={{ minHeight: '100%' }}
        >
            <Spin spinning={isLoading}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: "input",
                            label: "Input Data",
                            children: renderInputTab()
                        },
                        {
                            key: "output",
                            label: "Results",
                            children: renderOutputTab()
                        }
                    ]}
                />
            </Spin>
        </Card>
    );
};

export default LeastSquares;
